import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { KpiAmount } from '../shared/KpiAmount';
import { Package, Store, ChevronRight, Layers } from 'lucide-react';
import { normalizeExtraSummary, correctionBadge, correctionWorkflowIndicator } from '../../lib/extraSummary';

// COMPONENTE CANONICO — identità visiva sintetica di un Extra in tutto il CRM.
// SOLO presentazione: riceve un Extra e i valori canonici dal backend (via adapter).
// Il CORE (header + economics + status + CTA) è sempre identico; le sezioni possono
// aggiungere blocchi contestuali via `children` e azioni via `footerExtra`.
// NON è un editor: NON mostra le singole righe. Il dettaglio completo è nella pagina Extra.

const TYPE_TONE = {
  fornitura: 'bg-indigo-100 text-indigo-700',
  lavoro: 'bg-emerald-100 text-emerald-700',
};

export const ExtraSummaryDialog = ({
  open,
  onOpenChange,
  extra,
  onOpenFull,
  statusOverride = null,   // { label, cls } — override contestuale dello stato (es. stage scelte cliente)
  showEconomics = true,
  children = null,         // blocchi contestuali (fatturazione, pagamenti, appuntamento, ecc.)
  footerExtra = null,      // azioni contestuali aggiuntive nel footer
}) => {
  const s = extra ? normalizeExtraSummary(extra) : null;
  const cBadge = s ? correctionBadge(s.corrections) : null;
  const cWorkflow = s ? correctionWorkflowIndicator(s.corrections) : null;
  const hasCorr = !!(s && s.corrections && s.corrections.hasCorrections);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="extra-summary-dialog">
        {s && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 flex-wrap" data-testid="extra-summary-title">
                {s.title}
              </DialogTitle>
              <div className="flex items-center gap-2 flex-wrap text-xs mt-1">
                <span className="font-semibold text-[#171717]" data-testid="extra-summary-number">{s.number}</span>
                <span className={`px-2 py-0.5 rounded-full font-medium ${TYPE_TONE[s.type]}`}>{s.typeLabel}</span>
                {statusOverride ? (
                  <span className={`px-2 py-0.5 rounded-full font-medium ${statusOverride.cls || 'bg-slate-100 text-slate-600'}`} data-testid="extra-summary-status">{statusOverride.label}</span>
                ) : (
                  <span className="px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700" data-testid="extra-summary-status">{s.status}</span>
                )}
                {s.categoryLabel && (
                  <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600">{s.categoryLabel}</span>
                )}
                {cBadge && (
                  <span className={`px-2 py-0.5 rounded-full font-medium ${cBadge.cls}`} data-testid="extra-summary-correction-badge">{cBadge.label}</span>
                )}
              </div>
              {cWorkflow && (
                <div className="mt-1">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${cWorkflow.cls}`} data-testid="extra-summary-correction-workflow">{cWorkflow.label}</span>
                </div>
              )}
            </DialogHeader>

            {/* Fornitore (Extra fornitura) */}
            {s.type === 'fornitura' && s.supplier && (
              <div className="rounded-lg bg-[#FAFAFA] p-3 text-sm flex items-center gap-2" data-testid="extra-summary-supplier">
                <Store className="w-4 h-4 text-[#FF7819]" />
                <span className="font-medium text-[#171717]">{s.supplier}</span>
              </div>
            )}

            {/* Economics CORE — imponibile principale, IVA/Totale microcopy */}
            {showEconomics && !hasCorr && (
              <div className="rounded-lg border border-[#ECECEC] p-3 flex items-center justify-between" data-testid="extra-summary-economics">
                <div className="text-xs text-[#737373] flex items-center gap-1">
                  <Layers className="w-3.5 h-3.5" /> {s.itemsCount} {s.itemsCount === 1 ? 'voce' : 'voci'}
                </div>
                <KpiAmount
                  net={s.net}
                  vat={s.vat}
                  gross={s.gross}
                  vatRate={s.vatRate}
                  variant="extended"
                  valueClassName="text-2xl font-bold text-[#3C3C3C] leading-tight tabular-nums text-right"
                  className="text-right"
                  testId="extra-summary-kpi"
                />
              </div>
            )}

            {/* Economics con RETTIFICHE — valore CORRENTE principale + originario/rettifiche secondari */}
            {showEconomics && hasCorr && (
              <div className="rounded-lg border border-[#ECECEC] p-3 space-y-2" data-testid="extra-summary-economics-corrected">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-[#737373]">Imponibile corrente</div>
                  <KpiAmount
                    net={s.displayNet}
                    vat={s.displayVat}
                    gross={s.displayGross}
                    vatRate={s.vatRate}
                    variant="extended"
                    valueClassName="text-2xl font-bold text-[#3C3C3C] leading-tight tabular-nums text-right"
                    className="text-right"
                    testId="extra-summary-kpi-current"
                  />
                </div>
                <div className="pt-2 border-t border-dashed border-[#ECECEC] text-xs space-y-1">
                  <div className="flex items-center justify-between text-[#737373]">
                    <span>Imponibile originario</span>
                    <span className="tabular-nums" data-testid="extra-summary-original-net">{s.corrections.originalNet.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
                  </div>
                  <div className="flex items-center justify-between text-red-600 font-medium">
                    <span>Rettifiche</span>
                    <span className="tabular-nums" data-testid="extra-summary-corrections-net">{s.corrections.correctionsNet.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €</span>
                  </div>
                </div>
              </div>
            )}

            {/* Estensioni contestuali */}
            {children}

            <DialogFooter className="flex-wrap gap-2">
              {footerExtra}
              {onOpenFull && (
                <Button
                  onClick={() => { const id = s.id; onOpenChange(false); onOpenFull(id); }}
                  className="bg-[#FF7819] hover:bg-[#E56A0F]"
                  data-testid="extra-summary-open-full-btn"
                >
                  <Package className="w-4 h-4 mr-1" /> Apri dettaglio completo <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="extra-summary-close-btn">Chiudi</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExtraSummaryDialog;
